import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { OrganizationDetailsService } from '@services/organization-details.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { OrgSupplementalSystemActions } from '@store/actions/org-supplemental-system/org-supplemental-system.actions';
import { OrganizationEMRDetailsActions } from '@store/actions/organization-emr-details/organization-emr-details.actions';
import { OrganizationSupplementalSystem } from '@models/organizationSupplementalSystem.model';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import {
  selectSupplementalSystemOrgId,
  selectSupplementalSystems,
} from '@store/selectors/org-supplemental-system/org-supplemental-system.selector';
import { selectOrgId } from '@store/selectors/organization-emr-details/organization-emr-details.selector';
import { SYSTEM_TYPE } from '@constants/system-type.constants';
import { toDateString } from '../../../utils/date.util';
import { parseProblemDetailsError } from '../../../utils/api-error.util';

const ACTIVE_STATUS_ID = 1000;
const INACTIVE_STATUS_ID = 1001;

@Injectable()
export class OrgSupplementalSystemEffects {

  openAddPressed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrgSupplementalSystemActions.openAddPressed),
        tap(() => {
          import('@components/modals/select-system-type-modal/select-system-type-modal.component').then(
            ({ SelectSystemTypeModalComponent }) => {
              const dialogRef = this.dialog.open(SelectSystemTypeModalComponent, {
                width: '400px',
                disableClose: true,
              });
              dialogRef.afterClosed().subscribe((systemType: 'EHR' | 'PM/Billing' | undefined) => {
                if (!systemType) return;
                this.store.dispatch(OrgSupplementalSystemActions.openAddSystemTypeSelected({ systemType }));
              });
            }
          );
        })
      ),
    { dispatch: false }
  );

  openAddSystemTypeSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrgSupplementalSystemActions.openAddSystemTypeSelected),
        withLatestFrom(this.store.select(selectSupplementalSystems)),
        tap(([action, systems]) => {
          const hasActiveOfType = systems.some(
            (sys) => sys.statusID === ACTIVE_STATUS_ID && sys.systemType === action.systemType
          );
          if (hasActiveOfType) {
            const typeLabel =
              action.systemType === SYSTEM_TYPE.EHR
                ? 'EHR'
                : 'PM/Billing System';
            this.dialog.open(ConfirmationModalComponent, {
              data: {
                title: 'Active System Exists',
                content: `Only one ${typeLabel} can be active at a time. Please deactivate currently active ${typeLabel} to continue.`,
                confirmText: 'OK',
                cancelText: null,
              },
            });
          } else {
            this.openSupplementalSystemModal(action.systemType);
          }
        })
      ),
    { dispatch: false }
  );

  openEditPressed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrgSupplementalSystemActions.openEditPressed),
        tap((action) => this.openSupplementalSystemModal(action.system.systemType ?? SYSTEM_TYPE.EHR, action.system))
      ),
    { dispatch: false }
  );

  openDeletePressed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrgSupplementalSystemActions.openDeletePressed),
        tap((action) => {
          const dialogRef = this.dialog.open(ConfirmationModalComponent, {
            data: {
              title: 'Delete System',
              content: `Are you sure you want to delete: ${action.name}? This action cannot be undone.`,
              confirmText: 'Delete',
              cancelText: 'Cancel',
            },
          });
          dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              this.store.dispatch(
                OrgSupplementalSystemActions.deleteOrgSupplementalSystem({
                  id: action.id,
                  systemType: action.systemType,
                })
              );
            }
          });
        })
      ),
    { dispatch: false }
  );

  addSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrgSupplementalSystemActions.addOrgSupplementalSystem),
      withLatestFrom(this.store.select(selectSupplementalSystemOrgId)),
      switchMap(([action, orgId]) => {
        const data = action.data;
        return this.orgService
          .addSupplementalSystem({
            organizationID: parseInt(orgId),
            systemType: data.systemType,
            deliveryMethod: data.deliveryMethod,
            frequencyOfDelivery: data.frequencyOfDelivery === 'Other'
              ? (data.otherFrequencyOfDelivery || '')
              : data.frequencyOfDelivery,
            vendorName: data.vendorName,
            productVersion: data.productVersion,
            fileFormat: data.fileFormat,
            externalID: data.externalID,
            effectiveDate: toDateString(data.startDate),
          })
          .pipe(
            map((result) =>
              OrgSupplementalSystemActions.addOrgSupplementalSystemSuccess({
                data: { ...result, statusID: ACTIVE_STATUS_ID },
              })
            ),
            catchError((error: HttpErrorResponse) => {
              if (error?.status === 409) {
                return of(
                  OrgSupplementalSystemActions.addOrgSupplementalSystemConflict({
                    message: parseProblemDetailsError(
                      error,
                      'Only one system of this type can be active at a time. Please deactivate the currently active system to continue.'
                    ),
                  })
                );
              }
              return of(OrgSupplementalSystemActions.addOrgSupplementalSystemFailure({
                error: parseProblemDetailsError(error, 'Failed to add system'),
              }));
            })
          );
      })
    )
  );

  addOrgEMR$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationEMRDetailsActions.addOrganizationEMR),
      withLatestFrom(this.store.select(selectOrgId)),
      switchMap(([action, orgId]) => {
        const data = action.data;
        const isOtherFrequency = data.frequencyOfDelivery === 'Other';
        return this.orgService
          .addOrganizationEMR({
            organizationID: parseInt(orgId),
            emrID: data.emrID,
            productVersion: data.productVersion,
            ehrClinicalDeliveryMethod: data.deliveryMethod,
            ehrpmDeliveryMethod: null,
            frequencyOfDataDelivery: isOtherFrequency ? 'Other' : data.frequencyOfDelivery,
            otherFrequencyOfDataDelivery: isOtherFrequency ? data.otherFrequencyOfDelivery : null,
            fileFormat: data.fileFormat,
            externalID: data.externalID,
            effectiveDate: toDateString(data.startDate),
          })
          .pipe(
            map((result) => OrganizationEMRDetailsActions.addOrganizationEMRSuccess({ data: result })),
            catchError((error: HttpErrorResponse) => {
              if (error?.status === 409) {
                return of(
                  OrganizationEMRDetailsActions.addOrganizationEMRConflict({
                    message: parseProblemDetailsError(
                      error,
                      'Only one EHR can be active at a time. Please deactivate the currently active EHR to continue.'
                    ),
                  })
                );
              }
              return of(OrganizationEMRDetailsActions.addOrganizationEMRFailure({
                error: parseProblemDetailsError(error, 'Failed to add EHR'),
              }));
            })
          );
      })
    )
  );

  updateSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrgSupplementalSystemActions.updateOrgSupplementalSystem),
      withLatestFrom(this.store.select(selectSupplementalSystemOrgId)),
      switchMap(([action, orgId]) => {
        const form = action.form;
        const statusID = form.isActive ? ACTIVE_STATUS_ID : INACTIVE_STATUS_ID;
        const effectiveDate = toDateString(form.startDate);
        const expirationDate = toDateString(form.endDate);
        const isOtherFrequency = form.frequencyOfDelivery === 'Other';
        const resolvedFrequency = isOtherFrequency
          ? (form.otherFrequencyOfDelivery || '')
          : form.frequencyOfDelivery;

        const updatedSystem: OrganizationSupplementalSystem = {
          id: action.systemId,
          organizationID: action.orgId,
          systemType: action.systemType as 'EHR' | 'PM/Billing',
          deliveryMethod: form.deliveryMethod,
          frequencyOfDelivery: resolvedFrequency,
          vendorName: form.vendorName,
          productVersion: form.productVersion,
          fileFormat: form.fileFormat,
          externalID: form.externalID,
          statusID,
          effectiveDate,
          expirationDate,
          emrID: form.emrID ?? null,
          _legacyEhrpmDeliveryMethod: action.legacyEhrpmDeliveryMethod,
        };

        const apiCall = action.systemType === SYSTEM_TYPE.EHR
          ? this.orgService.updateOrganizationEMRDetails({
              id: action.systemId,
              organizationID: parseInt(orgId),
              emrID: form.emrID ?? null,
              vendorName: form.vendorName,
              productVersion: form.productVersion,
              ehrClinicalDeliveryMethod: form.deliveryMethod,
              ehrpmDeliveryMethod: action.legacyEhrpmDeliveryMethod ?? '',
              frequencyOfDataDelivery: isOtherFrequency ? 'Other' : form.frequencyOfDelivery,
              otherFrequencyOfDataDelivery: isOtherFrequency ? form.otherFrequencyOfDelivery : null,
              fileFormat: form.fileFormat,
              externalID: form.externalID,
              statusID,
              effectiveDate,
              expirationDate,
            })
          : this.orgService.updateSupplementalSystem(updatedSystem);

        return apiCall.pipe(
          map(() =>
            OrgSupplementalSystemActions.updateOrgSupplementalSystemSuccess({ system: updatedSystem })
          ),
          catchError((error: HttpErrorResponse) => {
            if (error?.status === 409) {
              return of(
                OrgSupplementalSystemActions.updateOrgSupplementalSystemConflict({
                  message: parseProblemDetailsError(
                    error,
                    'Only one system of this type can be active at a time. Please deactivate the currently active system to continue.'
                  ),
                })
              );
            }
            return of(
              OrgSupplementalSystemActions.updateOrgSupplementalSystemFailure({
                error: parseProblemDetailsError(error, 'Failed to update system'),
              })
            );
          })
        );
      })
    )
  );

  deleteSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrgSupplementalSystemActions.deleteOrgSupplementalSystem),
      switchMap((action) => {
        const apiCall = action.systemType === SYSTEM_TYPE.EHR
          ? this.orgService.deleteOrganizationEMR(String(action.id))
          : this.orgService.deleteSupplementalSystem(action.id);
        return apiCall.pipe(
          map(() =>
            OrgSupplementalSystemActions.deleteOrgSupplementalSystemSuccess({ id: action.id })
          ),
          catchError((error: HttpErrorResponse) =>
            of(OrgSupplementalSystemActions.deleteOrgSupplementalSystemFailure({
              error: parseProblemDetailsError(error, 'Failed to delete system'),
            }))
          )
        );
      })
    )
  );

  deleteSystemFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrgSupplementalSystemActions.deleteOrgSupplementalSystemFailure),
        tap((action) => {
          this.errorDialogService.showError(action.error, 'Delete Error');
        })
      ),
    { dispatch: false }
  );

  // Generic (non-conflict) save failures across add/update for both EHR and PM/Billing.
  // Conflict (409) cases are shown inline in the modal instead, since it's still open at that point.
  saveSystemFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          OrgSupplementalSystemActions.addOrgSupplementalSystemFailure,
          OrgSupplementalSystemActions.updateOrgSupplementalSystemFailure,
          OrganizationEMRDetailsActions.addOrganizationEMRFailure,
        ),
        tap((action) => {
          this.errorDialogService.showError(action.error, 'Save Error');
        })
      ),
    { dispatch: false }
  );

  private openSupplementalSystemModal(
    systemType: 'EHR' | 'PM/Billing',
    system?: OrganizationSupplementalSystem
  ): void {
    import('@components/modals/edit-add-org-ehr-modal/edit-add-org-ehr-modal.component').then(
      ({ EditAddOrgEhrModalComponent }) => {
        this.dialog.open(EditAddOrgEhrModalComponent, {
          width: '900px',
          maxWidth: '900px',
          disableClose: true,
          data: { system, systemType },
        });
      }
    );
  }

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly orgService: OrganizationDetailsService,
    private readonly dialog: MatDialog,
    private readonly errorDialogService: ErrorDialogService,
  ) {}
}
